// VRG-675 — react-query hooks for GTM → Sales and the source-channel field.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SourceChannel } from '@repo/types'

import { gtmApi } from 'services'

const CHANNELS_KEY = ['internal', 'gtm', 'source-channels'] as const

// VRG-691 — the Pipeline tab reads `useSalesBoard` (hooks/api/useGtmSalesPipeline.ts) now; the
// VRG-675 accounts-counted pipeline endpoint stays on the API for its guard, unused by the page.

/** Every recorded source channel. Small (one row per tagged account), so fetched whole. */
export const useSourceChannels = (enabled = true) =>
  useQuery({
    queryKey: CHANNELS_KEY,
    queryFn: () => gtmApi.sourceChannels(),
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  })

const useInvalidateChannels = () => {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: CHANNELS_KEY })
    // A channel feeds every deal's lead source, so the whole Sales cache moves with it.
    qc.invalidateQueries({ queryKey: ['internal', 'gtm', 'sales'] })
  }
}

export const useSetPersonSourceChannel = () => {
  const invalidate = useInvalidateChannels()
  return useMutation({
    mutationFn: ({ key, sourceChannel }: { key: string; sourceChannel: SourceChannel | null }) =>
      gtmApi.setPersonSourceChannel(key, sourceChannel),
    onSuccess: invalidate,
  })
}

export const useSetCompanySourceChannel = () => {
  const invalidate = useInvalidateChannels()
  return useMutation({
    mutationFn: ({ companyId, sourceChannel }: { companyId: string; sourceChannel: SourceChannel | null }) =>
      gtmApi.setCompanySourceChannel(companyId, sourceChannel),
    onSuccess: invalidate,
  })
}
